import { TypeGuard, type DocumentReference } from '@principle-theorem/shared';
import { isNumber, isString } from 'lodash';
import { IAppointment } from './appointment/appointment';
import { IAutomation } from './automation/automation';
import { IBrand } from './brand';
import { ITreatmentPlan } from './clinical-charting/treatment/treatment-plan';
import { ITreatmentStep } from './clinical-charting/treatment/treatment-step';
import { IInvoice } from './invoice/invoice';
import { IMarketingCampaignStepConversion } from './marketing/campaign-step-conversion';
import { IOrganisation } from './organisation/organisation';
import { IPatient } from './patient/patient';
import { IPractice } from './practice/practice';
import { TemplateScope } from './templating/template-scope';

export interface ITaskPayloadV1 {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  automationRef: DocumentReference<IAutomation<any>>;
  organisationRef: DocumentReference<IOrganisation>;
  brandRef: DocumentReference<IBrand>;
  patientRef: DocumentReference<IPatient>;
  treatmentPlanRef: DocumentReference<ITreatmentPlan>;
  treatmentStepRef: DocumentReference<ITreatmentStep>;
}

export type ITaskPayloadV2 = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  automationRef: DocumentReference<IAutomation<any>>;
  organisationRef: DocumentReference<IOrganisation>;
  brandRef: DocumentReference<IBrand>;
  patientRef: DocumentReference<IPatient>;
  practiceRef: DocumentReference<IPractice>;
  marketingCampaignStepConversionRef?: DocumentReference<IMarketingCampaignStepConversion>;
} & (
  | {
      scope: TemplateScope.Patient;
      patientRef: DocumentReference<IPatient>;
    }
  | {
      scope: TemplateScope.Appointment;
      treatmentPlanRef: DocumentReference<ITreatmentPlan>;
      treatmentStepRef: DocumentReference<ITreatmentStep>;
      appointmentRef: DocumentReference<IAppointment>;
    }
  | {
      scope: TemplateScope.TreatmentPlan;
      treatmentPlanRef: DocumentReference<ITreatmentPlan>;
    }
  | {
      scope: TemplateScope.TreatmentStep;
      treatmentPlanRef: DocumentReference<ITreatmentPlan>;
      treatmentStepRef: DocumentReference<ITreatmentStep>;
      appointmentRef?: DocumentReference<IAppointment>;
    }
  | {
      scope: TemplateScope.Invoice;
      invoiceRef: DocumentReference<IInvoice>;
    }
  | {
      scope: TemplateScope.None;
    }
);

export type ITaskPayload = ITaskPayloadV1 | ITaskPayloadV2;

export interface IDepositExpiryTaskPayload {
  depositRequestRef: string;
  expiresAtMillis: number;
}

export const isDepositExpiryTaskPayload =
  TypeGuard.interface<IDepositExpiryTaskPayload>({
    depositRequestRef: isString,
    expiresAtMillis: (data: unknown): data is number =>
      isNumber(data) && Number.isFinite(data) && Number.isInteger(data),
  });
